import { Capacitor } from '@capacitor/core';
import { buildGmailOauthReturnUrl, isNativeApp } from './nativeAppScheme';

/** Messages dismissed the re-auth modal; keep the banner until they close it. */
export const GMAIL_REAUTH_BANNER_KEY = 'gmail_reauth_banner';
/** Messages closed the banner; stay quiet for the rest of this app session. */
export const GMAIL_REAUTH_BANNER_CLOSED_KEY = 'gmail_reauth_banner_closed';
/** Set while Google sign-in is open so a cancelled return reopens the modal. */
export const GMAIL_REAUTH_PENDING_KEY = 'gmail_reauth_pending';

const GMAIL_SCOPE =
  'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email';

export type GmailOauthStartMode = 'popup' | 'redirect' | 'native';

function gmailCallbackUrl(): string {
  const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '');
  const supabaseUrlClean = supabaseUrl.replace('/rest/v1', '').replace(/\/$/, '');
  return `${supabaseUrlClean}/functions/v1/gmail-oauth-callback`;
}

/**
 * Start the same Google OAuth the Integrations Gmail modal uses.
 * `returnPath` is where the callback sends the user (web or iOS custom scheme).
 * Popup completion resolves when the popup closes or lands on success=gmail_connected.
 */
export async function startGmailOAuth(opts: {
  businessId: string;
  returnPath?: string;
}): Promise<GmailOauthStartMode> {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  if (!clientId) {
    throw new Error('Google Client ID not configured');
  }

  const returnUrl = buildGmailOauthReturnUrl(opts.returnPath || '/messages');
  const encodedState = btoa(
    JSON.stringify({
      businessId: opts.businessId,
      returnUrl,
    }),
  );
  const redirectUrl = gmailCallbackUrl();
  const authUrl =
    `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
    `&response_type=code&scope=${encodeURIComponent(GMAIL_SCOPE)}` +
    `&access_type=offline&prompt=consent&state=${encodeURIComponent(encodedState)}`;

  try {
    sessionStorage.setItem(GMAIL_REAUTH_PENDING_KEY, '1');
  } catch {
    /* ignore */
  }

  if (isNativeApp() || Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url: authUrl });
    return 'native';
  }

  const popup = window.open(authUrl, 'gmail-oauth', 'width=500,height=600');
  if (!popup) {
    window.location.assign(authUrl);
    return 'redirect';
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearInterval(timer);
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      resolve('popup');
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'GMAIL_OAUTH_SUCCESS') finish();
    };
    window.addEventListener('message', onMessage);
    const timer = window.setInterval(() => {
      if (popup.closed) {
        finish();
        return;
      }
      try {
        const popupUrl = popup.location.href;
        if (popupUrl.includes('success=gmail_connected')) {
          popup.close();
          finish();
        }
      } catch {
        // Cross-origin while the popup is still on Google.
      }
    }, 500);
    const timeout = window.setTimeout(() => {
      if (!popup.closed) popup.close();
      finish();
    }, 300000);
  });
}
